import { useCallback, useEffect, useRef } from 'react'

// Centralized scheduler for any async animation work.
// Keeps us from leaking timeouts/rAFs/listeners across unmounts or phase changes.
export default function useCancelableWork() {
  const rafIdsRef = useRef(new Set())
  const timeoutIdsRef = useRef(new Set())
  const cleanupFnsRef = useRef(new Set())

  const registerCleanup = useCallback((cleanupFn) => {
    // Register arbitrary cleanup (e.g., removeEventListener) and return an idempotent disposer.
    cleanupFnsRef.current.add(cleanupFn)
    return () => {
      if (!cleanupFnsRef.current.has(cleanupFn)) return
      cleanupFnsRef.current.delete(cleanupFn)
      cleanupFn()
    }
  }, [])

  const scheduleRaf = useCallback((callback) => {
    // rAF callback automatically de-registers itself once fired.
    let rafId = null
    rafId = requestAnimationFrame(() => {
      rafIdsRef.current.delete(rafId)
      callback()
    })
    rafIdsRef.current.add(rafId)
    return rafId
  }, [])

  const scheduleTimeout = useCallback((callback, delayMs) => {
    // Timeout callback automatically de-registers itself once fired.
    let timeoutId = null
    timeoutId = setTimeout(() => {
      timeoutIdsRef.current.delete(timeoutId)
      callback()
    }, delayMs)
    timeoutIdsRef.current.add(timeoutId)
    return timeoutId
  }, [])

  const clearAll = useCallback(() => {
    rafIdsRef.current.forEach((id) => cancelAnimationFrame(id))
    rafIdsRef.current.clear()

    timeoutIdsRef.current.forEach((id) => clearTimeout(id))
    timeoutIdsRef.current.clear()

    cleanupFnsRef.current.forEach((fn) => fn())
    cleanupFnsRef.current.clear()
  }, [])

  useEffect(() => {
    return () => {
      clearAll()
    }
  }, [clearAll])

  return { scheduleRaf, scheduleTimeout, registerCleanup, clearAll }
}
